/**
 * V4ExecuteInputModal — Phase 7g.
 *
 * Pre-Run input prompt. Activated when the workflow declares one or
 * more required inputs (trigger node `inputParameters` and/or external
 * workflow `input_schema`, merged server-side by
 * `WorkflowInputSchemaService` and surfaced via
 * GET /api/v1/workflows/{id}/required-inputs).
 *
 * Without this gate, runs of input-requiring workflows fail at the
 * first `${workflow.input.<name>}` resolution. V3 had a modal for this
 * (CanvasPageV3.jsx:4535) — V4 went without until 7g and silently
 * skipped inputs (CanvasPageV4.jsx:1069-1073).
 *
 * Contract:
 *   • Props are pure — modal owns no app state.
 *   • Submit is disabled until every required field is non-empty.
 *   • Enter submits when valid; Esc cancels.
 *   • Optional fields with empty values are stripped before submission
 *     (Conductor often validates "must not be empty" on optionals,
 *     which would defeat the purpose).
 *   • First required field auto-focuses.
 *
 * Field types supported:
 *   • string       — single-line input (default; unknown types coerce here)
 *   • number       — number input, validated as a Number
 *   • boolean      — checkbox
 *   • text|json    — textarea (multi-line)
 *
 * Tagged PHASE-7G.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Play, X } from 'lucide-react';
import {
  S, BRAND, SPACING, RADIUS, FONT, WEIGHT, SHADOW, Z,
  primaryBtn, secondaryBtn, ghostBtn, v4Input,
} from './v4Tokens';

const MONO = '"JetBrains Mono", "Fira Code", ui-monospace, monospace';

export default function V4ExecuteInputModal({
  open,
  params,           // [{name, required, type?, description?}, ...]
  values,           // { [name]: string|number|boolean }
  onChange,         // (nextValues) => void
  onSubmit,         // (trimmedValues) => void
  onCancel,         // () => void
}) {
  const firstInputRef = useRef(null);

  // Auto-focus the first required field on open. Defer until after the
  // DOM is mounted so the ref is populated.
  useEffect(() => {
    if (!open) return;
    const t = requestAnimationFrame(() => {
      if (firstInputRef.current) firstInputRef.current.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open]);

  // Required = params with required !== false. Spec defaults required
  // to true so a missing flag still gates Submit (mirrors V3 G182).
  const requiredParams = useMemo(
    () => (params || []).filter((p) => p && p.required !== false),
    [params],
  );

  const allFilled = useMemo(() => {
    for (const p of requiredParams) {
      const v = values?.[p.name];
      if (v === undefined || v === null) return false;
      if (typeof v === 'boolean') continue;              // booleans are always "filled"
      if (typeof v === 'number') {
        if (Number.isNaN(v)) return false;
        continue;
      }
      if (v.toString().trim().length === 0) return false;
    }
    return true;
  }, [requiredParams, values]);

  // Esc/Enter handled at the modal root so focus order doesn't matter.
  const onRootKey = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel?.();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && allFilled) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const setValue = (name, v) => {
    onChange?.({ ...(values || {}), [name]: v });
  };

  const handleSubmit = () => {
    if (!allFilled) return;
    // Strip empty optional fields. Sends only fields the operator filled
    // in — Conductor coerces undefined → use upstream default.
    const trimmed = {};
    (params || []).forEach((p) => {
      const v = values?.[p.name];
      if (v === undefined || v === null) return;
      if (typeof v === 'boolean') {
        trimmed[p.name] = v;
        return;
      }
      if (typeof v === 'number') {
        if (!Number.isNaN(v)) trimmed[p.name] = v;
        return;
      }
      const s = v.toString();
      if (s.trim().length > 0) trimmed[p.name] = s;
    });
    onSubmit?.(trimmed);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Workflow inputs"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
      onKeyDown={onRootKey}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: Z.modal,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '10vh',
      }}
    >
      <div style={{
        width: 'min(560px, 92vw)',
        background: '#ffffff',
        borderRadius: RADIUS.xl,
        boxShadow: SHADOW.popover,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        maxHeight: '80vh',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.s,
          padding: `${SPACING.m}px ${SPACING.l}px`,
          borderBottom: `0.5px solid ${S[200]}`,
        }}>
          <Play size={14} aria-hidden="true" style={{ color: BRAND.icon }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: FONT.lg, fontWeight: WEIGHT.semibold, color: S[900] }}>
              Workflow Inputs
            </div>
            <div style={{ fontSize: FONT.xs, color: S[500] }}>
              {(params || []).length} field{(params || []).length === 1 ? '' : 's'}
              {requiredParams.length !== (params || []).length
                ? ` · ${requiredParams.length} required`
                : ''}
              {' · referenced as '}
              <code style={{
                background: S[100], padding: '0 4px',
                borderRadius: RADIUS.sm, fontSize: FONT.xs, fontFamily: MONO,
              }}>
                {'${workflow.input.<name>}'}
              </code>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={ghostBtn}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: SPACING.l,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: SPACING.m,
        }}>
          {(params || []).map((p, idx) => (
            <Field
              key={p.name}
              param={p}
              value={values?.[p.name]}
              onChange={(v) => setValue(p.name, v)}
              onEnter={() => { if (allFilled) handleSubmit(); }}
              inputRef={idx === 0 ? firstInputRef : undefined}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.s,
          padding: `${SPACING.s + 2}px ${SPACING.l}px`,
          borderTop: `0.5px solid ${S[200]}`,
          background: S[50],
        }}>
          <span style={{ fontSize: FONT.xs, color: S[500] }}>
            {allFilled ? 'Ready to run' : 'Fill required fields to enable Run'}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACING.s }}>
            <button type="button" onClick={onCancel} style={secondaryBtn}>
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allFilled}
              style={{
                ...primaryBtn,
                opacity: allFilled ? 1 : 0.5,
                cursor: allFilled ? 'pointer' : 'not-allowed',
              }}
            >
              <Play size={11} aria-hidden="true" />
              Run Workflow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── Field renderer — branches on declared type ───────────────── */
function Field({ param, value, onChange, onEnter, inputRef }) {
  const required = param.required !== false;
  const type = (param.type || 'string').toLowerCase();
  const isMulti = type === 'json' || type === 'text' || type === 'object' || type === 'array';
  const isBool = type === 'boolean' || type === 'bool';
  const isNum  = type === 'number' || type === 'integer' || type === 'int' || type === 'float';

  const labelEl = (
    <label style={{
      display: 'flex', alignItems: 'center', gap: SPACING.xs,
      fontSize: FONT.sm, fontWeight: WEIGHT.semibold, color: S[700],
      marginBottom: SPACING.xs,
    }}>
      <span>{param.name}</span>
      {required && <span style={{ color: BRAND.icon }}>*</span>}
      {param.type && param.type !== 'string' && (
        <span style={{
          fontSize: FONT.xs, fontWeight: WEIGHT.medium, color: S[500],
          fontFamily: MONO,
        }}>
          {param.type}
        </span>
      )}
    </label>
  );

  const description = param.description
    ? (
      <div style={{
        fontSize: FONT.xs, color: S[500],
        marginBottom: SPACING.xs,
      }}>
        {param.description}
      </div>
    )
    : null;

  // Boolean → checkbox row
  if (isBool) {
    const checked = value === true || value === 'true';
    return (
      <div>
        {labelEl}
        {description}
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: SPACING.s,
          fontSize: FONT.md, color: S[700], cursor: 'pointer',
        }}>
          <input
            ref={inputRef}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          {checked ? 'true' : 'false'}
        </label>
      </div>
    );
  }

  // Multi-line → textarea (JSON/object/array/text)
  if (isMulti) {
    return (
      <div>
        {labelEl}
        {description}
        <textarea
          ref={inputRef}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.description || `Value for ${param.name}`}
          rows={4}
          style={{
            ...v4Input,
            fontFamily: MONO,
            resize: 'vertical',
            minHeight: 80,
          }}
        />
      </div>
    );
  }

  // Number → number input
  if (isNum) {
    return (
      <div>
        {labelEl}
        {description}
        <input
          ref={inputRef}
          type="number"
          value={value ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') onChange('');
            else {
              const n = Number(v);
              onChange(Number.isNaN(n) ? v : n);
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') onEnter?.(); }}
          placeholder={param.description || `Value for ${param.name}`}
          style={{ ...v4Input, fontFamily: MONO }}
        />
      </div>
    );
  }

  // Default: single-line string
  return (
    <div>
      {labelEl}
      {description}
      <input
        ref={inputRef}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') onEnter?.(); }}
        placeholder={param.description || `Value for ${param.name}`}
        autoComplete="off"
        spellCheck={false}
        style={{ ...v4Input, fontFamily: MONO }}
      />
    </div>
  );
}
